import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Box, IconButton, Typography, alpha } from "@mui/material";
import {
  MoreHoriz as MoreHorizIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  LocalHospital as ClinicBadgeIcon,
} from "@mui/icons-material";
import { HiOutlineMenu } from "react-icons/hi";
import { RxCross2 } from "react-icons/rx";
import logoVertical from "../assets/img/pupill_icon_vertical.png";
import logoHorizontal from "../assets/img/pupill_logo_horizontal.svg";
import { URL } from "../store/utils";
import useAuth from "../hooks/useAuth";
import { useActiveModules, moduleChecker } from "../hooks/useClinicModule";
import useTerms from "../hooks/useTerms";
import useMessagesUnreadCount from "../hooks/useMessagesUnreadCount";
import { useClinicTheme } from "../theme";
import { useLogoutMutation } from "../store/authApi";
import {
  FiChevronRight,
  FiGrid,
} from "react-icons/fi";
import {
  FaAppleAlt,
  FaCapsules,
  FaClinicMedical,
  FaCommentMedical,
  FaAllergies,
  FaNotesMedical,
  FaPhoneAlt,
  FaComments,
  FaVial,
  FaUsers,
  FaUserInjured,
  FaCalendarAlt,
  FaTabletAlt,
  FaImage,
  FaExclamationTriangle,
  FaHistory,
  FaChartBar,
  FaShieldAlt,
  FaCog,
  FaMoneyBillWave,
  FaLeaf,
  FaFileSignature,
  FaLayerGroup,
  FaBriefcase,
  FaSlidersH,
} from "react-icons/fa";
import { normalizeRole } from "../utils/roleLabels";
import { COLORS, TYPO } from "../styles/uiConstants";

// All possible ClinicStaff menu items with their required permissions
const CLINIC_STAFF_ITEMS = [
  {
    name: "Genel Bakış",
    to: "/Genel-Bakis",
    icon: <FiGrid className="w-6 h-6" />,
    permission: "dashboard:read",
  },
  { type: "section", name: "Hasta & Randevu" },
  {
    name: "Randevular",
    to: "/Randevular",
    icon: <FaCalendarAlt className="w-6 h-6" />,
    permission: "appointments:read",
  },
  {
    name: "Hastalar",
    to: "/Hastalar",
    icon: <FaUserInjured className="w-6 h-6" />,
    permission: "patients:read",
  },
  { type: "section", name: "Tıbbi Kayıtlar" },
  {
    name: "Tedaviler",
    to: "/Tedaviler",
    icon: <FaNotesMedical className="w-6 h-6" />,
    permission: "treatments:read",
  },
  {
    name: "Reçeteler",
    to: "/Receteler",
    icon: <FaCapsules className="w-6 h-6" />,
    permission: "prescriptions:read",
  },
  {
    name: "Tetkikler",
    to: "/Tetkikler",
    icon: <FaVial className="w-6 h-6" />,
    permission: "investigations:read",
  },
  {
    name: "Diyetler",
    to: "/Diyetler",
    icon: <FaAppleAlt className="w-6 h-6" />,
    permission: "diet:read",
  },
  {
    name: "Uygulamalar",
    to: "/Uygulamalar",
    icon: <FaLeaf className="w-6 h-6" />,
    permission: "sessions:read",
    module: "traditional",
  },
  { type: "section", name: "İletişim" },
  {
    name: "Telefon Görüşmeleri",
    to: "/Telefon-Gorusmeleri",
    icon: <FaPhoneAlt className="w-6 h-6" />,
    permission: "phoneCalls:read",
  },
  {
    name: "Mesajlar",
    to: "/Mesajlar",
    icon: <FaComments className="w-6 h-6" />,
    permission: "messages:read",
    module: "messaging",
    badge: "messages",
  },
  // Yönetim ve Rapor & Şablonlar açılır menü; açılınca altındakiler görünür.
  {
    type: "group",
    name: "Yönetim",
    icon: <FaBriefcase className="w-6 h-6" />,
    children: [
      {
        name: "Personel",
        to: "/Isletme-Performansi",
        icon: <FaUsers className="w-6 h-6" />,
        permission: "performance:read",
      },
      {
        name: "Finansal",
        to: "/Finansal",
        icon: <FaMoneyBillWave className="w-6 h-6" />,
        // Tahsilat (payments:read, billing modülü) veya Pupill Kullanımı (finance:read)
        permission: ["finance:read", "payments:read"],
      },
      {
        name: "Klinik",
        to: "/Klinik-Yonetimi",
        icon: <FaCog className="w-6 h-6" />,
        permission: ["clinic:appearance", "clinic:update", "services:manage", "whatsapp:manage"],
      },
    ],
  },
  // Raporlar ve klinik şablonları her zaman en altta tutulur.
  {
    type: "group",
    name: "Rapor & Şablonlar",
    icon: <FaLayerGroup className="w-6 h-6" />,
    children: [
      {
        name: "Raporlar",
        to: "/Raporlar",
        icon: <FaChartBar className="w-6 h-6" />,
        permission: "reports:read",
      },
      {
        name: "İlaç Şablonları",
        to: "/Klinik/Sablonlar/Ilaclar",
        icon: <FaCapsules className="w-6 h-6" />,
        permission: "doctorTemplates:read",
        module: "prescription",
      },
      {
        name: "Hastalık Şablonları",
        to: "/Klinik/Sablonlar/Hastaliklar",
        icon: <FaNotesMedical className="w-6 h-6" />,
        permission: "doctorTemplates:read",
        module: "anamnesis",
      },
      {
        name: "Diyet Şablonları",
        to: "/Klinik/Sablonlar/Diyetler",
        icon: <FaAppleAlt className="w-6 h-6" />,
        permission: "doctorTemplates:read",
        module: "diet",
      },
      {
        name: "Görüntüleme Şablonları",
        to: "/Klinik/Sablonlar/Goruntuleme",
        icon: <FaImage className="w-6 h-6" />,
        permission: "doctorTemplates:read",
        module: "investigation",
      },
      {
        name: "Laboratuvar Şablonları",
        to: "/Klinik/Sablonlar/Laboratuvar",
        icon: <FaVial className="w-6 h-6" />,
        permission: "doctorTemplates:read",
        module: "investigation",
      },
      {
        name: "Şikayet Şablonları",
        to: "/Klinik/Sablonlar/Sikayetler",
        icon: <FaExclamationTriangle className="w-6 h-6" />,
        permission: "doctorTemplates:read",
        module: "anamnesis",
      },
      {
        name: "Onam Formları",
        to: "/Klinik/Sablonlar/Onam-Formlari",
        icon: <FaFileSignature className="w-6 h-6" />,
        permission: "consents:manage",
        module: "traditional",
      },
    ],
  },
];

const ADMIN_MENU_ITEMS = [
  {
    name: "Dashboard",
    to: "/Admin-Dashboard",
    icon: <FiGrid className="w-6 h-6" />,
    permission: "admin-dashboard:read",
  },
  { type: "section", name: "Klinik & Cihaz" },
  {
    name: "Klinikler",
    to: "/Klinikler",
    icon: <FaClinicMedical className="w-6 h-6" />,
    permission: "admin-clinics:read",
  },
  {
    name: "Cihazlar",
    to: "/Cihazlar",
    icon: <FaTabletAlt className="w-6 h-6" />,
    permission: "admin-devices:read",
  },
  { type: "section", name: "Kullanıcılar" },
  {
    name: "Hastalar",
    to: "/Kullanici-Yonetimi/Hastalar",
    icon: <FaUserInjured className="w-6 h-6" />,
    permission: "users:read",
  },
  {
    name: "Klinik Kullanıcıları",
    to: "/Kullanici-Yonetimi/Klinik-Kullanicilari",
    icon: <FaClinicMedical className="w-6 h-6" />,
    permission: "users:read",
  },
  {
    name: "Personeller",
    to: "/Kullanici-Yonetimi/Personeller",
    icon: <FaUsers className="w-6 h-6" />,
    permission: "users:read",
  },
  {
    name: "Personel Şablonları",
    to: "/Kullanici-Yonetimi/Personel-Sablonlari",
    icon: <FaShieldAlt className="w-6 h-6" />,
    permission: "users:read",
  },
  {
    type: "group",
    name: "Sistem",
    icon: <FaCog className="w-6 h-6" />,
    children: [
      {
        name: "Denetim Günlüğü",
        to: "/Denetim-Gunlugu",
        icon: <FaHistory className="w-6 h-6" />,
        permission: "admin-audit:read",
      },
    ],
  },
  {
    type: "group",
    name: "Sistem Değişkenleri",
    icon: <FaSlidersH className="w-6 h-6" />,
    permission: "admin-system-variables:read",
    children: [
      {
        name: "Besinler",
        to: "/Sistem-Degiskenleri/Besinler",
        icon: <FaAppleAlt className="w-6 h-6" />,
        permission: "admin-system-variables:read",
      },
      {
        name: "Tanı Kategorileri",
        to: "/Sistem-Degiskenleri/Tani-Kategorileri",
        icon: <FaNotesMedical className="w-6 h-6" />,
        permission: "admin-system-variables:read",
      },
      {
        name: "Tanılar",
        to: "/Sistem-Degiskenleri/Tanilar",
        icon: <FaNotesMedical className="w-6 h-6" />,
        permission: "admin-system-variables:read",
      },
      {
        name: "Tahliller",
        to: "/Sistem-Degiskenleri/Tahliller",
        icon: <FaVial className="w-6 h-6" />,
        permission: "admin-system-variables:read",
      },
      {
        name: "İlaçlar",
        to: "/Sistem-Degiskenleri/Ilaclar",
        icon: <FaCapsules className="w-6 h-6" />,
        permission: "admin-system-variables:read",
      },
      {
        name: "Görüntüler",
        to: "/Sistem-Degiskenleri/Goruntuler",
        icon: <FaImage className="w-6 h-6" />,
        permission: "admin-system-variables:read",
      },
      {
        name: "Şikayetler",
        to: "/Sistem-Degiskenleri/Sikayetler",
        icon: <FaCommentMedical className="w-6 h-6" />,
        permission: "admin-system-variables:read",
      },
      {
        name: "Alerjiler",
        to: "/Sistem-Degiskenleri/Alerjiler",
        icon: <FaAllergies className="w-6 h-6" />,
        permission: "admin-system-variables:read",
      },
      {
        name: "Geleneksel Uygulamalar",
        to: "/Sistem-Degiskenleri/Geleneksel-Uygulamalar",
        icon: <FaLeaf className="w-6 h-6" />,
        permission: "admin-system-variables:read",
      },
    ],
  },
];

/**
 * İzin filtresinden sonra altında hiç öğe kalmayan bölüm başlıklarını atar —
 * yoksa yetkisi dar bir kullanıcı boş başlıklar görürdü. Bir başlık, bir
 * sonraki başlığa kadar en az bir gezinilebilir öğe varsa korunur.
 */
const dropEmptySections = (items) =>
  items.filter((item, index) => {
    if (item.type !== "section") return true;
    // Filtre sonrası bir başlığın hemen ardından gelen kayıt, o bölümün ilk
    // öğesidir. Orada başka bir başlık varsa (ya da liste bitmişse) bölüm boş
    // kalmış demektir.
    const next = items[index + 1];
    return Boolean(next) && next.type !== "section";
  });

const getMenuByRole = (role, permissions, hasModule = () => true) => {
  if (role === "Admin") {
    return ADMIN_MENU_ITEMS; // Admin sees all — no filtering
  }

  // Backend permission key'lerini lowercase'e normalize eder
  // (auth.middleware.normalizePermissionKey). Frontend de aynı şekilde
  // lowercase karşılaştırmalı, yoksa "doctorTemplates:read" gibi camelCase
  // key'ler gözükmez.
  const permSet = new Set((permissions || []).map((p) => String(p).toLowerCase()));
  // Dizi verilirse herhangi biri yeterli (ör. Klinik Yönetimi: görünüm veya modüller).
  const has = (key) =>
    !key ||
    (Array.isArray(key)
      ? key.some((k) => permSet.has(String(k).toLowerCase()))
      : permSet.has(String(key).toLowerCase()));

  // Açılır gruplarda süzgeç çocuklara uygulanır; çocuğu kalmayan grup düşer.
  const filterItems = (items, visible) =>
    items
      .map((item) =>
        item.type === "group"
          ? { ...item, children: (item.children || []).filter(visible) }
          : item
      )
      .filter((item) =>
        item.type === "group" ? has(item.permission) && item.children.length > 0 : visible(item)
      );

  if (role === "AdminStaff") {
    return dropEmptySections(filterItems(ADMIN_MENU_ITEMS, (item) => has(item.permission)));
  }

  // Klinik tarafındaki tüm roller (ClinicStaff, ClinicManager, Doctor, Secretary,
  // Pharmacist, Accountant, Dietitian) menüyü permission'a göre filtreler.
  // Pupill permission-bazlıdır; rol bazlı hardcoded ayrım yapılmaz.
  // Şablon başlıkları tek izne (doctorTemplates:read) bağlı; hangisinin görüneceğini
  // kliniğin modülleri belirler (ör. diyetisyende yalnız Diyet Şablonları).
  return dropEmptySections(
    filterItems(CLINIC_STAFF_ITEMS, (item) => has(item.permission) && hasModule(item.module))
  );
};

// Masaüstü rayının açık/kapalı genişlikleri. Mobilde ray hiç render edilmez —
// alt tab bar + bottom sheet kullanılır.
const DESKTOP_COLLAPSED_WIDTH = 72;
const DESKTOP_EXPANDED_WIDTH = 270;
// Alt tab bar yüksekliği. SiteLayout içeriğe bu kadar alt boşluk bırakır.
export const MOBILE_TABBAR_HEIGHT = 64;
const MOBILE_QUICK_LINK_COUNT = 5;

export default function Sidebar() {
  const { setAuth, auth } = useAuth();
  const { palette } = useClinicTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const [isExpanded, setIsExpanded] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const normalizedRole = normalizeRole(auth?.role || auth?.user?.role);
  const permissions = auth?.permissions || auth?.user?.permissions || [];
  const activeModules = useActiveModules();
  const terms = useTerms();
  const menu = getMenuByRole(normalizedRole, permissions, moduleChecker(activeModules)).map((item) =>
    // Klinik menüsünde terim seti (Hasta → Danışan). Admin menüsü klinik dışı; clinic yok → klinik seti.
    item.name === "Hastalar"
      ? { ...item, name: terms.patients }
      : item.name === "Hasta & Randevu"
        ? { ...item, name: `${terms.patient} & Randevu` }
        : item
  );
  // Menüde Mesajlar görünüyorsa (izin + modül) okunmamış konuşma sayısı rozetlenir.
  const unreadMessages = useMessagesUnreadCount(menu.some((i) => i.badge === "messages"));
  const badgeCountOf = (item) => (item?.badge === "messages" ? unreadMessages : 0);

  const navRef = useRef(null);
  const activeItemRef = useRef(null);
  const userMenuRef = useRef(null);

  // Mevcut path'e karşılık gelen group adı (bir group'un child'ı ise) ve
  // top-level non-group item olup olmadığı.
  const { activeParentGroup, currentTopIsNonGroup } = useMemo(() => {
    let parent = null;
    let topNonGroup = false;
    for (const item of menu) {
      if (item.type === "group" && Array.isArray(item.children)) {
        if (item.children.some((c) => c.to && location.pathname.startsWith(c.to))) {
          parent = item.name;
        }
      } else if (item.to && location.pathname.startsWith(item.to)) {
        topNonGroup = true;
      }
    }
    return { activeParentGroup: parent, currentTopIsNonGroup: topNonGroup };
  }, [menu, location.pathname]);

  // Aktif path bir group child'ı ise o grubu otomatik aç; group dışı bir
  // top-level item'daysak tüm açık grupları kapat.
  useEffect(() => {
    if (activeParentGroup) {
      setOpenGroups((prev) =>
        prev[activeParentGroup] ? prev : { ...prev, [activeParentGroup]: true }
      );
    } else if (currentTopIsNonGroup) {
      setOpenGroups((prev) => (Object.keys(prev).length === 0 ? prev : {}));
    }
  }, [activeParentGroup, currentTopIsNonGroup]);

  // Sidebar açıldığında aktif menü görünür alana scroll edilsin.
  useEffect(() => {
    if (!isExpanded) return;
    const t = setTimeout(() => {
      const el = activeItemRef.current;
      const scroller = navRef.current;
      if (!el || !scroller) return;
      const elRect = el.getBoundingClientRect();
      const scRect = scroller.getBoundingClientRect();
      const above = elRect.top < scRect.top;
      const below = elRect.bottom > scRect.bottom;
      if (above || below) {
        el.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }, 220);
    return () => clearTimeout(t);
  }, [isExpanded, location.pathname, openGroups]);

  // Kullanıcı menüsü dışına tıklanınca kapansın.
  useEffect(() => {
    if (!userMenuOpen) return;
    const onMouseDown = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [userMenuOpen]);

  // Bottom sheet açıkken arkadaki sayfa kaymasın.
  useEffect(() => {
    if (!sheetOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [sheetOpen]);

  // Use the logout mutation hook
  const [logoutUser] = useLogoutMutation();

  const firstName = auth?.user?.name || auth?.name || "";
  const lastName = auth?.user?.surname || auth?.surname || "";
  const displayName =
    `${firstName} ${lastName}`.trim() || auth?.user?.name || auth?.name || "Kullanıcı";
  const displayEmail = auth?.user?.email || auth?.email || "-";
  // Sidebar profil çubuğunda gösterilen rol etiketi — özel rol şablonu
  // varsa onun adını, yoksa normalize edilmiş rol etiketini kullanır.
  const rawRole = auth?.role || auth?.user?.role || "";
  const roleTemplateName =
    auth?.roleTemplate?.name ||
    auth?.user?.roleTemplate?.name ||
    null;
  const displayRole =
    roleTemplateName ||
    {
      Admin: "Sistem Yöneticisi",
      AdminStaff: "Admin Personeli",
      ClinicStaff: "Klinik Personeli",
      Patient: "Hasta",
      PatientRelative: "Hasta Yakını",
    }[rawRole] ||
    rawRole ||
    "";
  const clinicLogo =
    auth?.clinic?.logoUrl ||
    auth?.user?.clinic?.logoUrl ||
    auth?.clinic?.logo ||
    auth?.user?.clinic?.logo;
  const clinicLogoIcon =
    auth?.clinic?.logoIconUrl ||
    auth?.user?.clinic?.logoIconUrl ||
    auth?.clinic?.logoIcon ||
    auth?.user?.clinic?.logoIcon;
  // Menü açıkken varsayılan yatay logo; kapalı rayda dikey simge.
  const rawLogoSrc = clinicLogo || logoHorizontal;
  const rawIconLogoSrc = clinicLogoIcon || logoVertical;
  const normalizedLogoSrc =
    typeof rawLogoSrc === "string"
      ? rawLogoSrc.replace(/^\/+assets\//, "/assets/")
      : rawLogoSrc;
  const normalizedIconLogoSrc =
    typeof rawIconLogoSrc === "string"
      ? rawIconLogoSrc.replace(/^\/+assets\//, "/assets/")
      : rawIconLogoSrc;
  const baseUrl = String(URL.url || "").replace(/\/+$/, "");
  const logoSrc =
    typeof normalizedLogoSrc === "string" && normalizedLogoSrc.startsWith("/assets/")
      ? `${baseUrl}${normalizedLogoSrc}`
      : normalizedLogoSrc;
  const iconLogoSrc =
    typeof normalizedIconLogoSrc === "string" &&
      normalizedIconLogoSrc.startsWith("/assets/")
      ? `${baseUrl}${normalizedIconLogoSrc}`
      : normalizedIconLogoSrc;

  const closeSheet = () => setSheetOpen(false);

  const handleLogout = async () => {
    setUserMenuOpen(false);
    try {
      // Call logout API if user is authenticated
      if (auth?.accessToken) {
        await logoutUser({ token: auth.accessToken });
      }
    } catch (error) {
      console.error("Logout API error:", error);
    } finally {
      // Always clear local auth state regardless of API call result
      setAuth(null);
      navigate("/");
      setSheetOpen(false);
    }
  };

  // Aktif/pasif menü satırı — kutu yok: seçili satır tema renginde metin + altta
  // 2px tema rengi çizgi. Pasif satır hover'da yalnız metin rengi koyulaşır.
  // underline=false: yalnız renk vurgusu (açık grup başlığı gibi).
  // Üzerine gelince: tema renginde açık çizgi ortadan iki yana açılır, fare çekilince ortaya
  // doğru kapanır. Seçili satırda yok (o zaten tema renginde çizgili).
  // `edge`: çizginin satırın alt kenarına göre konumu (kenarlık kalınlığı kadar dışarıda).
  const hoverLineSx = (active, edge = 2) => ({
    position: "relative",
    "&::after": {
      content: '""',
      position: "absolute",
      left: "50%",
      right: "50%",
      bottom: -edge,
      height: 2,
      // Seçili çizgiden açık: tema rengi yarı saydam.
      bgcolor: alpha(palette.primary, 0.4),
      opacity: active ? 0 : 1,
      transition: "left 260ms ease, right 260ms ease",
      pointerEvents: "none",
    },
    "&:hover::after": active ? {} : { left: 0, right: 0 },
    "@media (prefers-reduced-motion: reduce)": { "&::after": { transition: "none" } },
  });

  const navItemSx = (active, expanded, underline = active) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: expanded ? "flex-start" : "center",
    gap: expanded ? 1.5 : 0,
    width: "100%",
    px: expanded ? 2 : 0,
    py: 1.25,
    my: 0.5,
    border: "none",
    borderRadius: 0,
    borderBottom: "2px solid",
    borderBottomColor: underline ? palette.primary : "transparent",
    bgcolor: "transparent",
    color: active ? palette.primary : COLORS.slate500,
    fontFamily: "inherit",
    fontWeight: active ? 600 : 500,
    fontSize: TYPO.body,
    textAlign: "left",
    cursor: "pointer",
    transition: "color 200ms ease, border-color 200ms ease",
    // Üzerine gelme seçiliden açık: metin ve çizgi tema renginin soluk tonu, çizgi animasyonla gelir.
    "&:hover": active ? {} : { color: alpha(palette.primary, 0.75) },
    ...hoverLineSx(underline),
  });

  const renderNavItem = (item, { expanded, onNavigate, attachRefs }) => {
    // Bölüm başlığı — tıklanmaz, katlanmaz; yalnızca altındaki öğeleri
    // adlandırır. Ray kapalıyken metin sığmadığı için ince bir ayraç çizilir.
    if (item?.type === "section") {
      return expanded ? (
        <Box
          key={`section-${item.name}`}
          sx={{
            // Sola yaslı: kendi yatay boşluğu yok, çizgi satır genişliğinde.
            mb: 0.5,
            pt: 2.5,
            pb: 0.75,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.08em",
            borderBottom: `1px solid ${COLORS.slate200}`,
            color: COLORS.slate400,
            textTransform: "uppercase",
            userSelect: "none",
          }}
        >
          {item.name}
        </Box>
      ) : (
        <Box
          key={`section-${item.name}`}
          aria-hidden
          sx={{ mx: 1.5, my: 1.25, borderTop: `1px solid ${COLORS.slate200}` }}
        />
      );
    }

    if (item?.type === "group") {
      const isOpen = Boolean(openGroups[item.name]);
      const isActiveGroup = item.name === activeParentGroup;
      // Grup yalnız bulunulan sayfayı içeriyorsa vurgulanır; sadece açık olması mavi yapmaz.
      const groupHighlighted = isActiveGroup;
      return (
        <Box key={item.name}>
          <Box
            component="button"
            type="button"
            onClick={() =>
              setOpenGroups((p) => ({ ...p, [item.name]: !p[item.name] }))
            }
            ref={attachRefs && isActiveGroup && !expanded ? activeItemRef : undefined}
            sx={{
              ...navItemSx(groupHighlighted, expanded, isActiveGroup && !expanded),
              justifyContent: expanded ? "space-between" : "center",
              // Açılır grup başlığı, bölüm başlıkları gibi ince bir çizgiyle ayrılır.
              // (borderBottomColor ayrıca verilir: navItemSx'teki "transparent" kısayolu ezer.)
              ...(expanded
                ? {
                    borderBottomWidth: "1px",
                    borderBottomColor: isActiveGroup ? palette.primary : COLORS.slate200,
                    ...hoverLineSx(isActiveGroup, 1),
                  }
                : {}),
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: expanded ? 1.5 : 0 }}>
              <Box sx={{ display: "flex", fontSize: 22 }}>{item.icon}</Box>
              {expanded ? <Box component="span">{item.name}</Box> : null}
            </Box>
            {expanded ? (
              <FiChevronRight
                size={16}
                style={{
                  flexShrink: 0,
                  transition: "transform 200ms ease",
                  transform: isOpen ? "rotate(90deg)" : "none",
                }}
              />
            ) : null}
          </Box>

          {isOpen && expanded ? (
            <Box sx={{ pl: 7, pr: 1, py: 0.5 }}>
              {(item.children || []).map((child) => {
                const isActiveChild = child.to && location.pathname.startsWith(child.to);
                return (
                  <NavLink
                    key={child.to}
                    to={child.to}
                    ref={attachRefs && isActiveChild ? activeItemRef : undefined}
                    onClick={onNavigate}
                    style={{ textDecoration: "none", display: "block" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        px: 1.5,
                        py: 1,
                        my: 0.25,
                        borderBottom: "2px solid",
                        borderBottomColor: isActiveChild ? palette.primary : "transparent",
                        color: isActiveChild ? palette.primary : COLORS.slate500,
                        fontWeight: isActiveChild ? 600 : 500,
                        fontSize: TYPO.body,
                        transition: "color 200ms ease, border-color 200ms ease",
                        "&:hover": isActiveChild ? {} : { color: alpha(palette.primary, 0.75) },
                        ...hoverLineSx(isActiveChild),
                      }}
                    >
                      <Box sx={{ display: "flex", fontSize: 18 }}>{child.icon}</Box>
                      {child.name}
                    </Box>
                  </NavLink>
                );
              })}
            </Box>
          ) : null}
        </Box>
      );
    }

    const isActiveTop = item.to && location.pathname.startsWith(item.to);
    return (
      <NavLink
        key={item.to}
        to={item.to}
        ref={attachRefs && isActiveTop ? activeItemRef : undefined}
        onClick={() => {
          // Top-level (group olmayan) bir item seçilirse açık grupları kapat.
          setOpenGroups((prev) => (Object.keys(prev).length === 0 ? prev : {}));
          onNavigate?.();
        }}
        style={{ textDecoration: "none", display: "block" }}
      >
        <Box sx={navItemSx(isActiveTop, expanded)}>
          <Badge
            color="error"
            variant={expanded ? "standard" : "dot"}
            badgeContent={badgeCountOf(item)}
            max={99}
            invisible={!badgeCountOf(item)}
            aria-label={badgeCountOf(item) ? `${badgeCountOf(item)} okunmamış konuşma` : undefined}
          >
            <Box sx={{ display: "flex", fontSize: 22 }}>{item.icon}</Box>
          </Badge>
          {expanded ? item.name : null}
        </Box>
      </NavLink>
    );
  };

  const renderNavList = (expanded, onNavigate, attachRefs) => (
    <Box
      ref={attachRefs ? navRef : undefined}
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        overflowX: "hidden",
        px: 1.5,
        "&::-webkit-scrollbar": { display: "none" },
        scrollbarWidth: "none",
      }}
    >
      {menu.map((item) => renderNavItem(item, { expanded, onNavigate, attachRefs }))}
    </Box>
  );

  const renderUserBlock = (expanded) => (
    <Box sx={{ mt: "auto", px: expanded ? 1 : 0.75, pt: 1, pb: 2, flexShrink: 0 }} ref={userMenuRef}>
      {/* Açılır menü kartın ÜSTÜNDE inline blok olarak açılır — absolute
          konumlandırma yok, böylece ray kaymaz/taşmaz. */}
      {userMenuOpen && expanded ? (
        <Box
          sx={{
            mb: 1,
            borderRadius: 3,
            overflow: "hidden",
            bgcolor: COLORS.white,
            border: `1px solid ${COLORS.slate200}`,
            boxShadow: "0 12px 32px rgba(15, 23, 42, 0.16)",
          }}
        >
          <NavLink
            to="/Profil"
            onClick={() => setUserMenuOpen(false)}
            style={{ textDecoration: "none", display: "block" }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 2,
                py: 1.5,
                color: COLORS.slate600,
                fontSize: TYPO.body,
                "&:hover": { bgcolor: COLORS.slate50, color: COLORS.slate800 },
              }}
            >
              <SettingsIcon sx={{ fontSize: 18 }} />
              Kullanıcı ayarları
            </Box>
          </NavLink>
          <Box sx={{ height: "1px", bgcolor: COLORS.slate100 }} />
          <Box
            component="button"
            type="button"
            onClick={handleLogout}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              width: "100%",
              px: 2,
              py: 1.5,
              border: "none",
              cursor: "pointer",
              bgcolor: "transparent",
              fontFamily: "inherit",
              fontSize: TYPO.body,
              textAlign: "left",
              color: COLORS.error,
              "&:hover": { bgcolor: COLORS.errorLightest, color: COLORS.errorDark },
            }}
          >
            <LogoutIcon sx={{ fontSize: 18 }} />
            Çıkış
          </Box>
        </Box>
      ) : null}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: expanded ? "flex-start" : "center",
          gap: expanded ? 1.25 : 0,
          borderRadius: 3,
          px: expanded ? 1.25 : 0,
          py: 1.25,
          bgcolor: COLORS.slate50,
          border: `1px solid ${userMenuOpen && expanded ? alpha(palette.primary, 0.3) : COLORS.slate200}`,
          transition: "border-color 200ms ease",
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: TYPO.body,
            bgcolor: palette.primary,
            color: palette.contrastText,
          }}
        >
          {(displayName || "K").charAt(0).toUpperCase()}
        </Box>

        {expanded ? (
          <>
            <Box sx={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column", gap: 0.25 }}>
              <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800, lineHeight: 1.2 }} noWrap>
                {displayName}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                <SettingsIcon sx={{ fontSize: 12, color: COLORS.slate400, flexShrink: 0 }} />
                <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500 }} noWrap>
                  {displayEmail}
                </Typography>
              </Box>
              {displayRole ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                  <ClinicBadgeIcon sx={{ fontSize: 12, color: palette.primary, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: TYPO.caption, fontWeight: 600, color: palette.primary }} noWrap>
                    {displayRole}
                  </Typography>
                </Box>
              ) : null}
            </Box>
            <IconButton
              onClick={() => setUserMenuOpen((p) => !p)}
              size="small"
              aria-label="Kullanıcı menüsü"
              sx={{
                flexShrink: 0,
                width: 32,
                height: 32,
                borderRadius: 2,
                color: userMenuOpen ? COLORS.slate700 : COLORS.slate400,
                bgcolor: userMenuOpen ? COLORS.slate200 : "transparent",
                "&:hover": { bgcolor: COLORS.slate200, color: COLORS.slate700 },
              }}
            >
              <MoreHorizIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </>
        ) : null}
      </Box>
    </Box>
  );

  // Logo her durumda yatayda ortalanır. Kapat butonu (bottom sheet) logoyu
  // kaydırmasın diye akıştan çıkarılıp sağa mutlak konumlanır.
  const renderLogo = (expanded, onClose) => (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // Yatay logo için sıkı alan: fazla dikey boşluk bırakmasın.
        height: expanded ? 72 : 64,
        px: expanded ? 2 : 0,
        flexShrink: 0,
      }}
    >
      <img
        src={expanded ? logoSrc : iconLogoSrc}
        alt="Logo"
        style={{
          width: "auto",
          maxWidth: expanded ? 180 : 40,
          maxHeight: expanded ? 52 : 40,
          objectFit: "contain",
          transition: "max-width 300ms ease, max-height 300ms ease",
        }}
      />
      {onClose ? (
        <IconButton
          aria-label="Menüyü kapat"
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            top: "50%",
            right: 20,
            transform: "translateY(-50%)",
            width: 32,
            height: 32,
            borderRadius: 2,
            bgcolor: COLORS.slate100,
            color: COLORS.slate500,
            "&:hover": { bgcolor: COLORS.slate200, color: COLORS.slate800 },
          }}
        >
          <RxCross2 size={16} />
        </IconButton>
      ) : null}
    </Box>
  );

  // Mobil alt tab bar kısayolları — group olmayan ilk N menü öğesi.
  const quickLinks = menu
    .filter((item) => item.type !== "group" && item.type !== "section")
    .slice(0, MOBILE_QUICK_LINK_COUNT);
  // Bottom sheet grid'i — gruplar çocuklarına açılarak düzleştirilir, bölüm
  // başlıkları (gezinilebilir hedefi yok) atlanır.
  const sheetLinks = menu.flatMap((item) => {
    if (item.type === "section") return [];
    return item.type === "group" ? item.children || [] : [item];
  });

  return (
    <>
      {/* Mobil: alt tab bar. Bottom sheet açıkken gizlenir. */}
      {!sheetOpen ? (
        <Box
          sx={{
            display: { xs: "block", md: "none" },
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.appBar + 1,
            bgcolor: alpha(COLORS.white, 0.95),
            backdropFilter: "blur(8px)",
            borderTop: `1px solid ${COLORS.slate200}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-around", px: 0.5, py: 0.75 }}>
            {quickLinks.map((link) => {
              const active = link.to && location.pathname.startsWith(link.to);
              return (
                <NavLink
                  key={link.to}
                  to={link.to}
                  style={{ textDecoration: "none", flex: 1, minWidth: 0 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.25,
                      py: 0.75,
                      px: 1,
                      borderRadius: 3,
                      color: active ? palette.primary : COLORS.slate400,
                      "&:active": { transform: "scale(0.92)" },
                      transition: "transform 150ms ease, color 200ms ease",
                    }}
                  >
                    <Box sx={{ display: "flex", fontSize: 20 }}>{link.icon}</Box>
                    <Typography sx={{ fontSize: 9, fontWeight: 600, lineHeight: 1 }} noWrap>
                      {link.name}
                    </Typography>
                    <Box
                      sx={{
                        width: 16,
                        height: 2,
                        mt: 0.25,
                        borderRadius: 1,
                        bgcolor: active ? palette.primary : "transparent",
                      }}
                    />
                  </Box>
                </NavLink>
              );
            })}
            <Box
              component="button"
              type="button"
              onClick={() => setSheetOpen(true)}
              aria-label="Menüyü aç"
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.25,
                flex: 1,
                minWidth: 0,
                py: 0.75,
                px: 1,
                border: "none",
                bgcolor: "transparent",
                cursor: "pointer",
                color: COLORS.slate400,
                fontFamily: "inherit",
                "&:active": { transform: "scale(0.92)" },
                transition: "transform 150ms ease",
              }}
            >
              <HiOutlineMenu size={22} />
              <Typography sx={{ fontSize: 9, fontWeight: 600, lineHeight: 1 }}>Menü</Typography>
              <Box sx={{ width: 16, height: 2, mt: 0.25 }} />
            </Box>
          </Box>
        </Box>
      ) : null}

      {/* Mobil: alttan açılan tam menü (bottom sheet) */}
      {sheetOpen ? (
        <Box sx={{ display: { xs: "block", md: "none" }, position: "fixed", inset: 0, zIndex: (theme) => theme.zIndex.drawer + 2 }}>
          <Box
            onClick={closeSheet}
            sx={{ position: "absolute", inset: 0, bgcolor: alpha(COLORS.black, 0.6), backdropFilter: "blur(4px)" }}
          />
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              maxHeight: "85vh",
              display: "flex",
              flexDirection: "column",
              bgcolor: COLORS.white,
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
            }}
          >
            {/* Tutamak */}
            <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5, pb: 0.5, flexShrink: 0 }}>
              <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: COLORS.slate300 }} />
            </Box>

            {renderLogo(true, closeSheet)}

            {/* Menü grid — 3 kolon */}
            <Box sx={{ flex: 1, overflowY: "auto", px: 2, pb: 1, minHeight: 0 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1 }}>
                {sheetLinks.map((link) => {
                  const active = link.to && location.pathname.startsWith(link.to);
                  return (
                    <NavLink
                      key={link.to}
                      to={link.to}
                      onClick={closeSheet}
                      style={{ textDecoration: "none" }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.75,
                          py: 1.75,
                          px: 1,
                          height: "100%",
                          borderRadius: 4,
                          bgcolor: active ? alpha(palette.primary, 0.1) : "transparent",
                          color: active ? palette.primary : COLORS.slate500,
                          "&:hover": active ? {} : { bgcolor: COLORS.slate100, color: COLORS.slate800 },
                          "&:active": { transform: "scale(0.95)" },
                          transition: "transform 150ms ease, background-color 200ms ease",
                        }}
                      >
                        <Box sx={{ display: "flex", fontSize: 20 }}>{link.icon}</Box>
                        <Typography
                          sx={{ fontSize: 11, fontWeight: 500, textAlign: "center", lineHeight: 1.2 }}
                        >
                          {link.name}
                        </Typography>
                      </Box>
                    </NavLink>
                  );
                })}
              </Box>
            </Box>

            {/* Kullanıcı + ayarlar */}
            <Box sx={{ flexShrink: 0, mx: 2, borderTop: `1px solid ${COLORS.slate200}` }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2.5,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    bgcolor: palette.primary,
                    color: palette.contrastText,
                  }}
                >
                  {(displayName || "K").charAt(0).toUpperCase()}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.25 }}>
                  <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800, lineHeight: 1.2 }} noWrap>
                    {displayName}
                  </Typography>
                  <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500 }} noWrap>
                    {displayEmail}
                  </Typography>
                  {displayRole ? (
                    <Typography sx={{ fontSize: TYPO.caption, fontWeight: 600, color: palette.primary }} noWrap>
                      {displayRole}
                    </Typography>
                  ) : null}
                </Box>
                <NavLink to="/Profil" onClick={closeSheet} style={{ textDecoration: "none" }}>
                  <IconButton
                    size="small"
                    aria-label="Kullanıcı ayarları"
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      bgcolor: COLORS.slate100,
                      color: COLORS.slate500,
                      "&:hover": { bgcolor: COLORS.slate200, color: COLORS.slate800 },
                    }}
                  >
                    <SettingsIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </NavLink>
                <IconButton
                  onClick={handleLogout}
                  size="small"
                  aria-label="Çıkış"
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    bgcolor: COLORS.errorLightest,
                    color: COLORS.error,
                    "&:hover": { bgcolor: COLORS.errorLight, color: COLORS.errorDark },
                  }}
                >
                  <LogoutIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>
            <Box sx={{ height: 8, flexShrink: 0 }} />
          </Box>
        </Box>
      ) : null}

      {/* Masaüstü: kalıcı ray, hover ile daralır/genişler (md ve üstü). */}
      <Box
        component="aside"
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => {
          setIsExpanded(false);
          setUserMenuOpen(false);
          // Açılıp seçim yapılmayan gruplar kapanır; yalnız bulunulan sayfanın grubu açık kalır.
          setOpenGroups(activeParentGroup ? { [activeParentGroup]: true } : {});
        }}
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          position: "fixed",
          top: 0,
          left: 0,
          height: "100vh",
          width: isExpanded ? DESKTOP_EXPANDED_WIDTH : DESKTOP_COLLAPSED_WIDTH,
          transition: "width 300ms ease",
          bgcolor: COLORS.white,
          borderRight: `1px solid ${COLORS.slate200}`,
          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      >
        {renderLogo(isExpanded, null)}
        {renderNavList(isExpanded, undefined, true)}
        {renderUserBlock(isExpanded)}
      </Box>
    </>
  );
}
